import React, { useState } from 'react';

const GatewayForm = ({ gateway, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    name: gateway?.name || '',
    username: gateway?.username || '',
    password: '',
    apiKey: '',
    gatewayId: gateway?.gatewayId || '',
    midNumber: gateway?.midNumber || ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const url = gateway ? `/api/gateways/${gateway.id}` : '/api/gateways';
      const method = gateway ? 'PUT' : 'POST';

      // Only include password if it's provided
      const body = { ...formData };
      if (!body.password && gateway) {
        delete body.password;
      }
      if (!body.apiKey) {
        delete body.apiKey;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save gateway');
      }

      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-xl font-bold text-gray-900">
            {gateway ? 'Edit Gateway' : 'Add New Gateway'}
          </h3>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Gateway Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Gateway Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="e.g., Main Gateway"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-smooth"
            />
          </div>

          {/* Username */}
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
              NMI Username *
            </label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              placeholder="Your NMI username"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-smooth"
            />
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              NMI Password {gateway ? '' : '*'}
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required={!gateway}
              placeholder={gateway ? 'Leave blank to keep current' : 'Your NMI password'}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-smooth"
            />
            {gateway && (
              <p className="mt-1 text-xs text-gray-500">Leave blank to keep current password</p>
            )}
          </div>

          {/* API Key (Optional) */}
          <div>
            <label htmlFor="apiKey" className="block text-sm font-medium text-gray-700 mb-2">
              API Key (Optional)
            </label>
            <input
              type="text"
              id="apiKey"
              name="apiKey"
              value={formData.apiKey}
              onChange={handleChange}
              placeholder="Optional API key"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-smooth"
            />
          </div>

          {/* Gateway ID (Optional) */}
          <div>
            <label htmlFor="gatewayId" className="block text-sm font-medium text-gray-700 mb-2">
              Gateway ID (Optional)
            </label>
            <input
              type="text"
              id="gatewayId"
              name="gatewayId"
              value={formData.gatewayId}
              onChange={handleChange}
              placeholder="Custom identifier for API lookups"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-smooth"
            />
            <p className="mt-1 text-xs text-gray-500">Used by inbound API to identify this gateway</p>
          </div>

          {/* MID Number (Optional) */}
          <div>
            <label htmlFor="midNumber" className="block text-sm font-medium text-gray-700 mb-2">
              MID Number (Optional)
            </label>
            <input
              type="text"
              id="midNumber"
              name="midNumber"
              value={formData.midNumber}
              onChange={handleChange}
              placeholder="e.g., 998300050112"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-smooth"
            />
            <p className="mt-1 text-xs text-gray-500">Processor MID number for CB360 VAMP rate calculation</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm">❌ {error}</p>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-smooth"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-smooth"
            >
              {loading ? 'Saving...' : gateway ? 'Update' : 'Add Gateway'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GatewayForm;
